import React, { useState, useEffect } from 'react';

interface BlurredThumbnailProps {
  videoId: string;
  roomId?: string; // Make roomId optional
  duration: number; // Duration in seconds for full deblur
  isPlaying: boolean; // Only deblur when song is playing
}

const BlurredThumbnail: React.FC<BlurredThumbnailProps> = ({ videoId, roomId, duration, isPlaying }) => {
  const [blurAmount, setBlurAmount] = useState(20); // Start with high blur (px)
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);

  // Load the thumbnail URL
  useEffect(() => {
    if (videoId) {
      // Include roomId in the URL if it exists
      const url = roomId 
        ? `/api/youtube/thumbnail/${videoId}?roomId=${roomId}`
        : `/api/youtube/thumbnail/${videoId}`;
      setThumbnailUrl(url);
      setBlurAmount(20); // Reset blur amount when video changes
    }
  }, [videoId, roomId]);

  // Gradually deblur the image when playing
  useEffect(() => {
    if (!isPlaying || !thumbnailUrl) return;

    // Calculate steps to go from 20px blur to 0px blur
    const intervalTime = 500; // Update every 500ms
    const steps = (duration * 1000) / intervalTime;
    const decrementAmount = 20 / steps;

    const interval = setInterval(() => {
      setBlurAmount((prevBlur) => {
        const newBlur = Math.max(0, prevBlur - decrementAmount);
        return newBlur;
      });
    }, intervalTime);

    return () => clearInterval(interval);
  }, [isPlaying, duration, thumbnailUrl]);

  // If no thumbnail URL, don't render anything
  if (!thumbnailUrl) {
    return null;
  }

  return (
    <div className="blurred-thumbnail">
      <img 
        src={thumbnailUrl} 
        alt="Song thumbnail" 
        style={{ filter: `blur(${blurAmount}px)` }} 
      />
    </div>
  );
};

export default BlurredThumbnail; 